import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/validators/user.schema';

/**
 * Anonymous-locale persistence via the `NEXT_LOCALE` cookie (next-intl's
 * conventional name). This is the source of truth for logged-out visitors and
 * is kept in sync with the authenticated account preference by the
 * LanguageSwitcher. Read server-side in the `(public)` layout via
 * `next/headers cookies()` for flash-free SSR; written client-side here.
 *
 * NOT HttpOnly — the client switcher must write it.
 */
export const LOCALE_COOKIE = 'NEXT_LOCALE';

const ONE_YEAR_SECONDS = 31536000;

function isSupportedLocale(value: string | undefined | null): value is SupportedLocale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Read the locale cookie on the client. Returns the locale only if it is a
 * supported value, otherwise null (caller falls back to 'en').
 */
export function readLocaleCookie(): SupportedLocale | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${LOCALE_COOKIE}=`));
  if (!match) return null;
  let value: string;
  try {
    value = decodeURIComponent(match.slice(LOCALE_COOKIE.length + 1));
  } catch {
    // A malformed cookie value (e.g. a stray `%`) makes decodeURIComponent
    // throw; treat it as "no valid locale" rather than crashing the caller.
    return null;
  }
  return isSupportedLocale(value) ? value : null;
}

/**
 * Write the locale cookie on the client. `path=/` so every route sees it,
 * 1-year max-age, SameSite=Lax. Not HttpOnly by design.
 */
export function writeLocaleCookie(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  // Add `Secure` over HTTPS (prod) but not on http (local dev), so the cookie
  // isn't silently dropped by the browser in development.
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
}
